/* ============================================================================
   TPW STORE HOURS — SINGLE SOURCE OF TRUTH  (assets/tpw-store-hours.js)
   ----------------------------------------------------------------------------
   One canonical engine for "is the store open?", "when does it ship?" and
   "when can I collect?". Every surface (announcement bar, hero status chips,
   header drawer, footer dispatch desk, PDP postcode board / delivery rows,
   the Genius Bar element) must consume THIS module instead of re-deriving the
   maths. If a fact below changes, change it HERE and nowhere else.

   CANONICAL FACTS (founder, 2026-06-24)
   -------------------------------------
   • Opening hours (store + Click & Collect), Melbourne wall-clock, open 7 days:
       Mon–Fri  11:00–19:00   (11am–7pm)
       Sat–Sun  11:00–18:00   (11am–6pm)
   • Public holidays: the STORE IS OPEN at the normal hours for that weekday,
     BUT Australia Post does not ship on public holidays. So a public holiday is
     NOT a business/dispatch day, yet the store + Click & Collect stay OPEN.
   • Same-day Australia Post dispatch cut-off: 2:00pm on a business day
     (Mon–Fri excluding public holidays). After 2pm / weekend / public holiday
     → ships the next business day.
   • Click & Collect: ready in ~60 min while the store is open AND there is at
     least 60 min before close. Ordered late (under ~60 min before close) or
     while closed → ready the next open day, roughly after 12pm.

   PUBLIC HOLIDAYS are VIC dates (the dispatch origin is Melbourne). They are
   hard-coded ISO strings below and MUST BE REFRESHED YEARLY — see HOLIDAYS.

   API (all time inputs optional; default = real "now"; pass a Date to simulate)
   ----------------------------------------------------------------------------
     TPWStoreHours.isOpen(now)              -> Boolean  (store hours; holiday-agnostic)
     TPWStoreHours.closingTimeToday(now)    -> { hour, minutes, label, minsUntilClose, open }
     TPWStoreHours.nextOpen(now)            -> { relative, weekday, label, parts }
     TPWStoreHours.isBusinessDay(date)      -> Boolean  (Mon–Fri && !public holiday)
     TPWStoreHours.isPublicHoliday(date)    -> Boolean
     TPWStoreHours.isWeekend(date)          -> Boolean
     TPWStoreHours.openStatus(now)          -> { open, closingSoon, minsUntilClose, minsUntilOpen, closeLabel, openLabel, statusLabel }
     TPWStoreHours.dispatchETA(now)         -> { sameDay, secondsToCutoff, shipParts, relative, shipsLabel, orderByLabel }
     TPWStoreHours.clickCollectETA(now)     -> { sameDay, soon, relative, readyParts, label }
     TPWStoreHours.businessDaysFromDispatch(now, n) -> parts   (delivery date arithmetic)
     TPWStoreHours.dispatchOrigin(now)      -> parts   (today if shipping today, else next business day)

   Lower-level helpers (also exported): melbParts, iso, fmtWeekdayShort,
   fmtWeekdayLong, fmtDayShort (e.g. "9 Jun"), fmtDayMonthWeekday ("Tue 9 Jun"),
   fmtClock12 (19 -> "7pm"), CUTOFF_HOUR, HOURS.

   "parts" object shape (Melbourne calendar fields for a given instant):
     { wd:'Tue', dow:2, y:2026, m:6, d:9, h:13, mn:5, s:0, minutes:785, iso:'2026-06-09' }
     dow: 0=Sun … 6=Sat (JS convention).
   ========================================================================== */
(function () {
  'use strict';
  if (window.TPWStoreHours) return; // single definition wins

  var TZ = 'Australia/Melbourne';
  var DAY_MS = 86400000;
  var CUTOFF_HOUR = 14; // 2:00pm same-day AusPost dispatch cut-off

  // Store opening hours by day. close is the hour the store shuts (24h).
  var HOURS = {
    weekday: { open: 11, close: 19 }, // Mon–Fri 11am–7pm
    weekend: { open: 11, close: 18 }  // Sat–Sun 11am–6pm
  };

  // Click & Collect: a same-day pickup needs at least this many minutes of
  // open trading left before close.
  var CC_MIN_LEAD_MIN = 60;
  // When pickup rolls to the next open day, it's "roughly after 12pm".
  var CC_READY_HOUR = 12;

  /* --------------------------------------------------------------------------
     PUBLIC HOLIDAYS — VIC (Melbourne dispatch origin). AusPost does NOT ship on
     these; the STORE stays open. ISO 'YYYY-MM-DD'. Hard-coded for easy editing.

     ⚠️ REFRESH YEARLY (do it each December for the next year). Also add the AFL
     Grand Final Friday once the AFL confirms the date — it floats (late Sep /
     early Oct) and is a VIC public holiday, so AusPost is closed that day.
     Source of truth for VIC dates: business.vic.gov.au public holidays.
     -------------------------------------------------------------------------- */
  var HOLIDAYS = {
    // 2026
    '2026-01-01': "New Year's Day",
    '2026-01-26': 'Australia Day',
    '2026-03-09': 'Labour Day',
    '2026-04-03': 'Good Friday',
    '2026-04-04': 'Saturday before Easter Sunday',
    '2026-04-05': 'Easter Sunday',
    '2026-04-06': 'Easter Monday',
    '2026-04-25': 'ANZAC Day',
    '2026-06-08': "King's Birthday",
    '2026-09-25': 'AFL Grand Final Friday', // VERIFY date each year (AFL-set)
    '2026-11-03': 'Melbourne Cup Day',
    '2026-12-25': 'Christmas Day',
    '2026-12-26': 'Boxing Day',
    '2026-12-28': 'Boxing Day (observed)',
    // 2027
    '2027-01-01': "New Year's Day",
    '2027-01-26': 'Australia Day',
    '2027-03-08': 'Labour Day',
    '2027-03-26': 'Good Friday',
    '2027-03-27': 'Saturday before Easter Sunday',
    '2027-03-28': 'Easter Sunday',
    '2027-03-29': 'Easter Monday',
    '2027-04-25': 'ANZAC Day',
    '2027-06-14': "King's Birthday",
    '2027-11-02': 'Melbourne Cup Day',
    '2027-12-25': 'Christmas Day',
    '2027-12-26': 'Boxing Day',
    '2027-12-27': 'Christmas Day (observed)',
    '2027-12-28': 'Boxing Day (observed)',
    // 2028
    '2028-01-01': "New Year's Day",
    '2028-01-03': "New Year's Day (observed)",
    '2028-01-26': 'Australia Day',
    '2028-03-13': 'Labour Day',
    '2028-04-14': 'Good Friday',
    '2028-04-15': 'Saturday before Easter Sunday',
    '2028-04-16': 'Easter Sunday',
    '2028-04-17': 'Easter Monday',
    '2028-04-25': 'ANZAC Day',
    '2028-06-12': "King's Birthday",
    '2028-11-07': 'Melbourne Cup Day',
    '2028-12-25': 'Christmas Day',
    '2028-12-26': 'Boxing Day'
    // 2029+ : ADD BEFORE 2029. Until then, holidays simply aren't skipped and
    // the customer-facing copy ("allow extra time around public holidays") covers it.
  };

  var WEEKDAY_LONG = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
  var DOW_FROM_WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* ---- Melbourne wall-clock via Intl (DST-safe; no fragile string parsing) ---- */
  var _fmt = new Intl.DateTimeFormat('en-AU', {
    timeZone: TZ, weekday: 'short', year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  });

  function melbParts(now) {
    var o = {};
    _fmt.formatToParts(now || new Date()).forEach(function (p) { o[p.type] = p.value; });
    var h = +o.hour % 24; // some engines emit "24" for midnight
    var p = {
      wd: o.weekday,
      dow: DOW_FROM_WD[o.weekday],
      y: +o.year, m: +o.month, d: +o.day,
      h: h, mn: +o.minute, s: +o.second,
      minutes: h * 60 + (+o.minute)
    };
    p.iso = p.y + '-' + pad2(p.m) + '-' + pad2(p.d);
    return p;
  }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function iso(now) { return melbParts(now).iso; }

  function isWeekend(now) { var w = melbParts(now).wd; return w === 'Sat' || w === 'Sun'; }
  function isPublicHoliday(now) { return Object.prototype.hasOwnProperty.call(HOLIDAYS, melbParts(now).iso); }
  function isBusinessDay(now) { return !isWeekend(now) && !isPublicHoliday(now); }
  function holidayName(now) { return HOLIDAYS[melbParts(now).iso] || null; }

  function hoursFor(now) { return isWeekend(now) ? HOURS.weekend : HOURS.weekday; }

  function isOpen(now) {
    // Store hours are holiday-agnostic: open 7 days, public holidays included.
    var p = melbParts(now);
    var hrs = isWeekend(now) ? HOURS.weekend : HOURS.weekday;
    return p.minutes >= hrs.open * 60 && p.minutes < hrs.close * 60;
  }

  /* ---- DST-safe day stepping: anchor to ~Melbourne noon so a 23h/25h DST day
     can never skip or repeat a calendar date when we add 86,400,000ms. ---- */
  function _noonAnchor(now) {
    var d = now || new Date();
    var p = melbParts(d);
    return new Date(d.getTime() + ((12 - p.h) * 60 - p.mn) * 60000 - p.s * 1000);
  }
  function _stepDays(anchor, k) { return new Date(anchor.getTime() + k * DAY_MS); }

  // Next calendar day that is a business (dispatch) day.
  function nextBusinessDay(now) {
    var a = _noonAnchor(now), k = 1, d;
    do { d = _stepDays(a, k); k++; } while (!isBusinessDay(d) && k < 60);
    return d;
  }
  // Date n business days after dispatch (n=0 -> the dispatch day itself).
  function addBusinessDays(now, n) {
    var a = _noonAnchor(now), k = 0, count = 0, d = a;
    while (count < n && k < 120) { k++; d = _stepDays(a, k); if (isBusinessDay(d)) count++; }
    return d;
  }
  // The day the parcel actually leaves: today if we can still ship today, else
  // the next business day.
  function dispatchOrigin(now) {
    var p = melbParts(now);
    if (isBusinessDay(now) && p.minutes < CUTOFF_HOUR * 60) return p;
    return melbParts(nextBusinessDay(now));
  }
  function businessDaysFromDispatch(now, n) {
    // Anchor arithmetic to the dispatch origin so transit days are counted from
    // the day the parcel leaves, not from "now".
    var origin = dispatchOrigin(now);
    // Rebuild a Date near the origin's Melbourne noon to step from.
    var base = _originToDate(origin);
    if (n <= 0) return origin;
    return melbParts(addBusinessDays(base, n));
  }
  // Turn a parts object back into a Date anchored ~Melbourne noon of that day.
  // Good enough for further day-stepping (we only read calendar fields after).
  function _originToDate(p) {
    // Construct from the ISO date at noon UTC, then nudge by the TZ offset via
    // re-reading parts. Simplest robust route: start from real now's anchor and
    // walk to the target date.
    var probe = _noonAnchor(new Date());
    var guard = 0;
    while (guard < 800) {
      var pp = melbParts(probe);
      if (pp.iso === p.iso) return probe;
      probe = new Date(probe.getTime() + (pp.iso < p.iso ? DAY_MS : -DAY_MS));
      guard++;
    }
    return probe;
  }

  /* ---- formatting ---- */
  function fmtWeekdayShort(p) { return (p && p.wd) || melbParts(p).wd; }
  function fmtWeekdayLong(p) { var wd = (p && p.wd) || melbParts(p).wd; return WEEKDAY_LONG[wd]; }
  function fmtDayShort(p) { p = p && p.iso ? p : melbParts(p); return p.d + ' ' + MONTHS[p.m - 1]; }            // "9 Jun"
  function fmtDayMonthWeekday(p) { p = p && p.iso ? p : melbParts(p); return p.wd + ' ' + p.d + ' ' + MONTHS[p.m - 1]; } // "Tue 9 Jun"
  function fmtClock12(h) { var x = h % 24; return (x % 12 || 12) + (x >= 12 ? 'pm' : 'am'); }

  // Relative wording for a target parts vs. "now": today / tomorrow / weekday.
  function relativeDay(targetParts, now) {
    var today = melbParts(now);
    if (targetParts.iso === today.iso) return 'today';
    var tomorrow = melbParts(new Date(_noonAnchor(now).getTime() + DAY_MS));
    if (targetParts.iso === tomorrow.iso) return 'tomorrow';
    return WEEKDAY_LONG[targetParts.wd];
  }

  /* ---- closing time today ---- */
  function closingTimeToday(now) {
    var p = melbParts(now);
    var hrs = hoursFor(now);
    var open = isOpen(now);
    return {
      hour: hrs.close,
      minutes: hrs.close * 60,
      label: fmtClock12(hrs.close),
      openHour: hrs.open,
      openLabel: fmtClock12(hrs.open),
      minsUntilClose: hrs.close * 60 - p.minutes, // negative if past close
      open: open
    };
  }

  /* ---- next open instant ---- */
  function nextOpen(now) {
    var p = melbParts(now);
    var hrs = hoursFor(now);
    // Opens later today (before opening, store opens today).
    if (p.minutes < hrs.open * 60) {
      return { relative: 'today', weekday: p.wd, label: 'Opens ' + fmtClock12(hrs.open) + ' today', parts: p };
    }
    // Otherwise the next calendar day (store opens 7 days).
    var nd = melbParts(_stepDays(_noonAnchor(now), 1));
    var rel = relativeDay(nd, now); // 'tomorrow' here
    var openHr = (nd.wd === 'Sat' || nd.wd === 'Sun') ? HOURS.weekend.open : HOURS.weekday.open;
    return {
      relative: rel,
      weekday: nd.wd,
      label: 'Opens ' + fmtClock12(openHr) + ' ' + (rel === 'tomorrow' ? 'tomorrow' : WEEKDAY_LONG[nd.wd]),
      parts: nd
    };
  }

  /* ---- consolidated open status (for the footer / drawer / chips) ---- */
  function openStatus(now) {
    var p = melbParts(now);
    var hrs = hoursFor(now);
    var open = isOpen(now);
    var minsUntilClose = hrs.close * 60 - p.minutes;
    var no = nextOpen(now);
    return {
      open: open,
      closingSoon: open && minsUntilClose <= 60,
      minsUntilClose: open ? minsUntilClose : 0,
      minsUntilOpen: open ? 0 : (p.minutes < hrs.open * 60 ? hrs.open * 60 - p.minutes : null),
      closeLabel: fmtClock12(hrs.close),
      openLabel: open ? 'Open now' : no.label,
      statusLabel: open
        ? (minsUntilClose <= 60 ? 'Open · closing in ' + minsUntilClose + ' min' : 'Open · until ' + fmtClock12(hrs.close))
        : ('Closed · ' + no.label.replace(/^Opens /, 'opens '))
    };
  }

  /* ---- same-day AusPost dispatch ---- */
  function dispatchETA(now) {
    var p = melbParts(now);
    var sameDay = isBusinessDay(now) && p.minutes < CUTOFF_HOUR * 60;
    if (sameDay) {
      var secs = CUTOFF_HOUR * 3600 - (p.minutes * 60 + p.s);
      return {
        sameDay: true,
        secondsToCutoff: Math.max(0, secs),
        shipParts: p,
        relative: 'today',
        shipsLabel: 'Ships today',
        orderByLabel: 'Order by ' + fmtClock12(CUTOFF_HOUR) + ' for same-day dispatch'
      };
    }
    var ship = melbParts(nextBusinessDay(now));
    var rel = relativeDay(ship, now);
    return {
      sameDay: false,
      secondsToCutoff: 0,
      shipParts: ship,
      relative: rel,
      shipsLabel: rel === 'tomorrow' ? 'Ships tomorrow' : 'Ships ' + WEEKDAY_LONG[ship.wd],
      orderByLabel: 'Ships ' + (rel === 'tomorrow' ? 'tomorrow' : (rel === 'today' ? 'today' : WEEKDAY_LONG[ship.wd]))
    };
  }

  /* ---- Click & Collect ---- */
  function clickCollectETA(now) {
    var p = melbParts(now);
    var hrs = hoursFor(now);
    var open = isOpen(now);
    var minsToClose = hrs.close * 60 - p.minutes;

    // Same-day pickup: open AND at least the lead time before close.
    if (open && minsToClose >= CC_MIN_LEAD_MIN) {
      return { sameDay: true, soon: true, relative: 'today', readyParts: p, label: 'Ready in ~60 min' };
    }

    // Otherwise it rolls to the next OPEN day, ready ~after 12pm. The store is
    // open 7 days (holidays included), so the next open day is:
    //   • today  — if we're before opening time today (e.g. ordered at 9am)
    //   • tomorrow — if we're open-but-too-late or already past close
    var readyParts;
    if (!open && p.minutes < hrs.open * 60) {
      readyParts = p; // store opens later today
    } else {
      readyParts = melbParts(_stepDays(_noonAnchor(now), 1)); // next calendar day
    }
    var rel = relativeDay(readyParts, now);
    var when = rel === 'today' ? 'today' : (rel === 'tomorrow' ? 'tomorrow' : WEEKDAY_LONG[readyParts.wd]);
    return {
      sameDay: rel === 'today',
      soon: false,
      relative: rel,
      readyParts: readyParts,
      label: 'Ready ' + when + ' after ' + fmtClock12(CC_READY_HOUR)
    };
  }

  window.TPWStoreHours = {
    // facts (read-only references)
    TZ: TZ,
    CUTOFF_HOUR: CUTOFF_HOUR,
    HOURS: HOURS,
    HOLIDAYS: HOLIDAYS,
    // predicates
    isOpen: isOpen,
    isWeekend: isWeekend,
    isPublicHoliday: isPublicHoliday,
    isBusinessDay: isBusinessDay,
    holidayName: holidayName,
    // status / timing
    closingTimeToday: closingTimeToday,
    nextOpen: nextOpen,
    openStatus: openStatus,
    dispatchETA: dispatchETA,
    clickCollectETA: clickCollectETA,
    // date arithmetic (delivery ranges)
    dispatchOrigin: dispatchOrigin,
    nextBusinessDay: function (now) { return melbParts(nextBusinessDay(now)); },
    businessDaysFromDispatch: businessDaysFromDispatch,
    // low-level + formatters
    melbParts: melbParts,
    iso: iso,
    relativeDay: relativeDay,
    fmtWeekdayShort: fmtWeekdayShort,
    fmtWeekdayLong: fmtWeekdayLong,
    fmtDayShort: fmtDayShort,
    fmtDayMonthWeekday: fmtDayMonthWeekday,
    fmtClock12: fmtClock12
  };
})();
